import { rivermeadowApi } from '../api';

export const discoveryInventoryApi = rivermeadowApi.injectEndpoints({
    endpoints: (builder) => ({
        uploadDiscoveryImportFile: builder.mutation({
            query: ({ file, analysisFile, orgId, importName, toolId }) => {
                const formData = new FormData();
                const isJsonFile = file?.type === 'application/json';
                let discoveryUploadFile = file;
                if (isJsonFile) {
                    discoveryUploadFile =  new Blob([file], { type: 'application/json' });
                }
                if (analysisFile) {
                    formData.append('analysisFile', analysisFile);
                }
                formData.append('discoveryUploadFile', discoveryUploadFile, file.name);
                formData.append('organizationId', orgId);
                formData.append('discoveryToolName', toolId);
                if (importName) {
                    formData.append('importName', importName);
                }

                return {
                    url: '/discovery',
                    method: 'POST',
                    body: formData,
                    headers: {
                        Accept: 'application/json',
                    },
                };
            },
        }),
        getImportHistory: builder.query({
            query: ({ orgId, toolId }) => {
                let url = `discovery/${orgId}/import-history`;
                if (toolId) {
                    url = `${url}?tool_identifier=${toolId}`
                }
                return {
                    url,
                    method: 'GET',
                }
            },
        }),
        getInventoryVMInfo: builder.query({
            query: ({
                orgId,
                size = 10,
                page = 0,
                sort,
                combined_search,
                migration_ready_status,
                cpu_utilization,
                ram_utilization,
                unassigned,
            }) => ({
                url: `discovery/${orgId}/normalized-vm-info`,
                method: 'GET',
                params: {
                    size,
                    page,
                    ...(sort && { sort }),
                    ...(combined_search && { combined_search }),
                    ...(migration_ready_status && { migration_ready_status }),
                    ...(cpu_utilization && { cpu_utilization }),
                    ...(ram_utilization && { ram_utilization }),
                    ...(unassigned && { unassigned_to_move_group: true, unassigned_to_application: true }),
                },
            }),
            keepUnusedDataFor: 0, // Do not cache data
            providesTags: ['FILTERED_DISCOVERY_INVENTORY'],
        }),
        getInventorySummary: builder.query({
            query: (orgId) => ({
                url: `discovery/${orgId}/summary`,
                method: 'GET',
            }),
            providesTags: ['DISCOVERY_INVENTORY'],
        }),
        getModernizationSavings: builder.query({
            query: () => ({
                url: 'discovery/modernization-savings',
                method: 'GET',
            }),
        }),
        updateInventoryStatusAndCriteria: builder.mutation({
            query: ({ orgId, inventoryIds, status, criteria }) => {
                const body = {};
                if (status) {
                    body.status = status;
                }
                if (criteria) {
                    body.criteria = criteria;
                }
                if (inventoryIds.length > 0) {
                    body.ids = inventoryIds;
                }

                return {
                    url: `discovery/${orgId}/normalized-vm-info/migration-ready`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: ['DISCOVERY_INVENTORY', 'FILTERED_DISCOVERY_INVENTORY'],
        }),
        addRemoveIgnoredOptimizations: builder.mutation({
            query: ({ orgId, vmIds, action, optimizations }) => ({
                url: `discovery/${orgId}/normalized-vm-info/ignored-optimizations`,
                method: 'PUT',
                body: {
                    ids: vmIds,
                    action,
                    ignored_optimizations: optimizations,
                },
            }),
            invalidatesTags: ['DISCOVERY_INVENTORY', 'FILTERED_DISCOVERY_INVENTORY'],
        }),
        updateOptimizations: builder.mutation({
            query: ({ orgId, vmId, optimizations }) => ({
                url: `discovery/${orgId}/normalized-vm-info/optimizations`,
                method: 'PUT',
                body: {
                    optimizations,
                    id: vmId,
                },
            }),
            invalidatesTags: ['DISCOVERY_INVENTORY', 'FILTERED_DISCOVERY_INVENTORY'],
        }),
        getInstanceTypes: builder.query({
            query: ({ cloudType = 'aws' }) => ({
                url: `discovery/instance-types`,
                method: 'GET',
                params: {
                    cloud_type: cloudType,
                },
            }),
        }),
        sendToMigration: builder.mutation({
            query: ({ orgId, vmIds, overrideExisting = false, moveGroupId }) => ({
                url: `discovery/normalized-vm-info/migrate`,
                method: 'POST',
                body: {
                    organization_id: orgId,
                    ids: vmIds,
                    override_existing: overrideExisting,
                    move_group_id: moveGroupId,
                },
            }),
        }),
    }),
});

export const {
    useGetImportHistoryQuery,
    useUploadDiscoveryImportFileMutation,
    useGetInventoryVMInfoQuery,
    useLazyGetInventoryVMInfoQuery,
    useGetInventorySummaryQuery,
    useLazyGetInventorySummaryQuery,
    useUpdateInventoryStatusAndCriteriaMutation,
    useAddRemoveIgnoredOptimizationsMutation,
    useSendToMigrationMutation,
    useUpdateOptimizationsMutation,
    useGetInstanceTypesQuery,
    useLazyGetInstanceTypesQuery,
    useGetModernizationSavingsQuery,
} = discoveryInventoryApi;
